/**
 * Shared sort dropdown menu for search modals
 *
 * Renders an absolute-positioned dropdown with sort field options
 * and ascending/descending direction items. Styled with Add Player's
 * polished dark mode tokens (primary-500 / primary-400).
 * The trigger button is NOT included — each consumer renders its own
 * trigger inside a Box[data-sort-dropdown] wrapper.
 * @module components/shared/search-modal/SortDropdown/SortDropdownView
 */

import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

/** Props for a single sort direction item */
interface SortDirectionItemProps {
  /** Whether no sort field is selected (disables the item) */
  readonly isDisabled: boolean;
  /** Whether this direction is currently active */
  readonly isActive: boolean;
  /** Handler when pressed */
  readonly onPress: () => void;
  /** Display label */
  readonly label: string;
}

/** Container class when disabled */
const DIRECTION_DISABLED_CLASS =
  "flex-row items-center justify-between px-3 py-2 opacity-40";
/** Container class when active */
const DIRECTION_ACTIVE_CLASS =
  "flex-row items-center justify-between px-3 py-2 bg-surface-raised";
/** Container class when idle */
const DIRECTION_IDLE_CLASS =
  "flex-row items-center justify-between px-3 py-2 web:hover:bg-surface-raised";

/**
 * Resolves the container class for a sort direction item based on state
 * @param isDisabled - Whether the item is disabled
 * @param isActive - Whether this direction is active
 */
const getDirectionContainerClass = (
  isDisabled: boolean,
  isActive: boolean
): string => {
  if (isDisabled) return DIRECTION_DISABLED_CLASS;
  if (isActive) return DIRECTION_ACTIVE_CLASS;
  return DIRECTION_IDLE_CLASS;
};

/** Text class for disabled state */
const DIRECTION_TEXT_DISABLED = "text-body text-content-subtle";
/** Text class for active state — uses primary tokens instead of hardcoded hex */
const DIRECTION_TEXT_ACTIVE =
  "text-body font-medium text-needs-semantic-primary-500 dark:text-needs-semantic-primary-400";
/** Text class for idle state */
const DIRECTION_TEXT_IDLE = "text-body text-content-neutral-strong";

/**
 * Resolves the text class for a sort direction item
 * @param isDisabled - Whether the item is disabled
 * @param isActive - Whether this direction is active
 */
const getDirectionTextClass = (
  isDisabled: boolean,
  isActive: boolean
): string => {
  if (isDisabled) return DIRECTION_TEXT_DISABLED;
  if (isActive) return DIRECTION_TEXT_ACTIVE;
  return DIRECTION_TEXT_IDLE;
};

/**
 * Renders a single sort direction item (Ascending or Descending)
 * @param props - Direction item state and handlers
 * @param props.isDisabled - Whether no sort field is selected
 * @param props.isActive - Whether this direction is currently active
 * @param props.onPress - Handler when pressed
 * @param props.label - Display label
 */
const renderSortDirectionItem = ({
  isDisabled,
  isActive,
  onPress,
  label,
}: SortDirectionItemProps) => (
  <Pressable
    onPress={onPress}
    disabled={isDisabled}
    className={getDirectionContainerClass(isDisabled, isActive)}
  >
    <Text
      variant="unstyled"
      className={getDirectionTextClass(isDisabled, isActive)}
    >
      {label}
    </Text>
    {!isDisabled && isActive ? (
      <Text
        variant="body-bold"
        className="text-needs-semantic-primary-500 dark:text-needs-semantic-primary-400"
      >
        ✓
      </Text>
    ) : null}
  </Pressable>
);

/** Props for SortDropdownView */
interface SortDropdownViewProps {
  /** Whether the dropdown is open */
  readonly isOpen: boolean;
  /** Available sort options */
  readonly sortOptions: readonly {
    readonly field: string;
    readonly label: string;
  }[];
  /** Currently selected sort field — null when no explicit selection */
  readonly selectedSortField: string | null;
  /** Currently selected sort direction */
  readonly sortDirection: "ASC" | "DESC";
  /** Pre-built handlers keyed by sort field */
  readonly sortHandlers: Readonly<Record<string, () => void>>;
  /** Toggle ascending */
  readonly onAscending: () => void;
  /** Toggle descending */
  readonly onDescending: () => void;
}

/**
 * Sort dropdown menu with field options and direction items.
 * Uses ScrollView for the options list so it scrolls on both web and native
 * (overflow-y-auto is web-only and breaks native rendering).
 * @param props - Component props
 * @param props.isOpen - Whether dropdown is visible
 * @param props.sortOptions - Available sort field options
 * @param props.selectedSortField - Current sort field
 * @param props.sortDirection - Current direction
 * @param props.sortHandlers - Pre-built sort handlers
 * @param props.onAscending - Ascending handler
 * @param props.onDescending - Descending handler
 */
const SortDropdownView = ({
  isOpen,
  sortOptions,
  selectedSortField,
  sortDirection,
  sortHandlers,
  onAscending,
  onDescending,
}: SortDropdownViewProps) =>
  isOpen ? (
    <Stack className="absolute right-0 top-full z-50 mt-1 w-52 overflow-hidden rounded-none border border-outline-default bg-surface-base shadow-hard-5">
      <ScrollView className="max-h-64">
        <Stack>
          {sortOptions.map(option => (
            <Pressable
              key={option.field}
              onPress={sortHandlers[option.field]}
              className={`flex-row items-center justify-between px-3 py-2 ${
                selectedSortField === option.field
                  ? "bg-surface-raised"
                  : "web:hover:bg-surface-raised"
              }`}
            >
              <Text
                variant={
                  selectedSortField === option.field ? "body-strong" : "body"
                }
                className={
                  selectedSortField === option.field
                    ? "text-needs-semantic-primary-500 dark:text-needs-semantic-primary-400"
                    : "text-content-neutral-strong"
                }
              >
                {option.label}
              </Text>
              {selectedSortField === option.field ? (
                <Text
                  variant="body-bold"
                  className="text-needs-semantic-primary-500 dark:text-needs-semantic-primary-400"
                >
                  ✓
                </Text>
              ) : null}
            </Pressable>
          ))}
        </Stack>
      </ScrollView>
      <Box className="h-px bg-outline-default" />
      {renderSortDirectionItem({
        isDisabled: selectedSortField === null,
        isActive: sortDirection === "ASC",
        onPress: onAscending,
        label: "Ascending",
      })}
      {renderSortDirectionItem({
        isDisabled: selectedSortField === null,
        isActive: sortDirection === "DESC",
        onPress: onDescending,
        label: "Descending",
      })}
    </Stack>
  ) : null;

SortDropdownView.displayName = "SortDropdownView";

export default memo(SortDropdownView);
